/**
 * ConfigFocusedEditor — Sidebar + Detail Panel editor for existing configs
 *
 * Layout: left sidebar with process/step tree, right panel with focused edit view.
 * Supports read mode (published version with toggles) and edit mode (draft).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePlan } from '../../context/PlanContext';
import ProcessForm from './ProcessForm';
import StepForm from './StepForm';
import ValidationSummary from './ValidationSummary';
import useConfigValidation from './hooks/useConfigValidation';
import {
  EMPTY_STEP, EMPTY_PROCESS, EMPTY_IDENTIFIER, EMPTY_CONDITION, EMPTY_CUSTOM_FIELD,
} from './utils/configTransforms';

export default function ConfigFocusedEditor({
  draft,  // useConfigDraft return value
  isEditing,
  onStartEdit,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getLimit } = usePlan();
  const isAdmin = user?.role === 'admin';

  const {
    version, processes, setProcesses, saving, error,
    success, saveStatus, lastSavedAt, draftCreatedFrom,
    saveDraft, saveAndPublish, discardDraft, toggleField,
  } = draft;

  const { errors, validate } = useConfigValidation();

  // Sidebar selection
  const [selectedProcess, setSelectedProcess] = useState(0);
  const [selectedStep, setSelectedStep] = useState(0);
  const [selectedType, setSelectedType] = useState('step'); // 'process' | 'step'
  const [expandedProcesses, setExpandedProcesses] = useState(new Set([0]));

  // Dialogs
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  if (!version || !processes) return null;

  // --- Process/step update helpers ---
  function updateProcess(pi, field, value) {
    setProcesses(prev => prev.map((p, i) => i === pi ? { ...p, [field]: value } : p));
  }
  function updateStep(pi, si, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) => j === si ? { ...s, [field]: value } : s) } : p));
  }
  function addStep(pi) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: [...p.steps, EMPTY_STEP()] } : p));
  }
  function removeStep(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.filter((_, j) => j !== si) } : p));
    if (selectedStep >= processes[pi].steps.length - 1) {
      setSelectedStep(Math.max(0, processes[pi].steps.length - 2));
    }
  }
  function addProcess() {
    setProcesses(prev => [...prev, EMPTY_PROCESS()]);
  }
  function removeProcess(pi) {
    setProcesses(prev => prev.filter((_, i) => i !== pi));
    if (selectedProcess >= processes.length - 1) {
      setSelectedProcess(Math.max(0, processes.length - 2));
      setSelectedStep(0);
    }
  }
  function addIdentifier(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: [...s.identifiers, EMPTY_IDENTIFIER()] } : s) } : p));
  }
  function removeIdentifier(pi, si, ii) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: s.identifiers.filter((_, k) => k !== ii) } : s) } : p));
  }
  function updateIdentifier(pi, si, ii, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: s.identifiers.map((id, k) =>
          k === ii ? { ...id, [field]: value } : id) } : s) } : p));
  }
  function addCondition(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: [...s.conditions, EMPTY_CONDITION()] } : s) } : p));
  }
  function removeCondition(pi, si, ci) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: s.conditions.filter((_, k) => k !== ci) } : s) } : p));
  }
  function updateCondition(pi, si, ci, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: s.conditions.map((c, k) =>
          k === ci ? { ...c, [field]: value } : c) } : s) } : p));
  }
  function addCustomField(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: [...s.custom_fields, EMPTY_CUSTOM_FIELD()] } : s) } : p));
  }
  function removeCustomField(pi, si, fi) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: s.custom_fields.filter((_, k) => k !== fi) } : s) } : p));
  }
  function updateCustomField(pi, si, fi, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: s.custom_fields.map((cf, k) =>
          k === fi ? { ...cf, [field]: value } : cf) } : s) } : p));
  }

  // --- Sidebar ---
  function toggleProcess(pi) {
    setExpandedProcesses(prev => {
      const next = new Set(prev);
      if (next.has(pi)) next.delete(pi);
      else next.add(pi);
      return next;
    });
  }

  function selectProcess(pi) {
    setSelectedProcess(pi);
    setSelectedType('process');
    if (!expandedProcesses.has(pi)) toggleProcess(pi);
  }

  function selectStep(pi, si) {
    setSelectedProcess(pi);
    setSelectedStep(si);
    setSelectedType('step');
  }

  // --- Actions ---
  async function handleSaveAndClose() {
    const ok = await saveDraft();
    if (ok) navigate('/config/history');
  }

  async function handleSaveAndPublish() {
    const result = validate(processes);
    if (!result.valid) {
      setShowValidation(true);
      return;
    }
    const ok = await saveAndPublish();
    if (ok) {
      setTimeout(() => navigate('/config/history'), 1800);
    }
  }

  async function handleDiscard() {
    const ok = await discardDraft();
    if (ok) navigate('/config/history');
  }

  function handleNavigateToError(pi, si, field) {
    if (pi !== null) setSelectedProcess(pi);
    if (si !== null) {
      setSelectedStep(si);
      setSelectedType('step');
      if (!expandedProcesses.has(pi)) toggleProcess(pi);
    } else {
      setSelectedType('process');
    }
    setShowValidation(false);
  }

  // Save status text
  function saveStatusText() {
    if (saveStatus === 'saving') return 'Saving…';
    if (saveStatus === 'error') return 'Save failed — retrying';
    if (saveStatus === 'saved' && lastSavedAt) {
      const seconds = Math.round((Date.now() - lastSavedAt.getTime()) / 1000);
      if (seconds < 5) return 'Draft saved just now';
      if (seconds < 60) return `Draft saved ${seconds}s ago`;
      return `Draft saved ${Math.round(seconds / 60)}m ago`;
    }
    return null;
  }

  const readOnly = !isEditing;
  const currentProcess = processes[selectedProcess];
  const currentStep = selectedType === 'step' && currentProcess ? currentProcess.steps[selectedStep] : null;

  const maxProcesses = getLimit('max_processes');
  const atProcessLimit = maxProcesses != null && processes.length >= maxProcesses;

  return (
    <div className="config-editor-layout">
      {/* Top bar */}
      {isEditing && (
        <div className="config-topbar">
          <div className="flex items-center gap-12">
            <span style={{ fontSize: '14px', fontWeight: '600' }}>v{version.version_number}</span>
            <span className="badge badge-warning">Draft</span>
            {draftCreatedFrom && (
              <span className="text-secondary" style={{ fontSize: '12px' }}>Based on v{draftCreatedFrom} (published)</span>
            )}
            {saveStatusText() && (
              <>
                <span style={{ opacity: 0.3 }}>·</span>
                <span style={{
                  fontSize: '12px',
                  color: saveStatus === 'error' ? 'var(--danger)' : saveStatus === 'saving' ? 'var(--warning)' : 'var(--success)'
                }}>
                  {saveStatusText()}
                </span>
              </>
            )}
          </div>
          <div className="flex gap-8">
            <button className="btn btn-ghost text-danger" onClick={() => setShowDiscardDialog(true)} disabled={saving}>
              Discard Draft
            </button>
            <button className="btn btn-secondary" onClick={handleSaveAndClose} disabled={saving}>
              Save & Close
            </button>
            <button className="btn btn-primary" onClick={handleSaveAndPublish} disabled={saving}>
              {saving ? 'Saving…' : 'Save & Publish'}
            </button>
          </div>
        </div>
      )}

      {/* Read mode header */}
      {!isEditing && (
        <div className="config-topbar">
          <div className="flex items-center gap-12">
            <span style={{ fontSize: '14px', fontWeight: '600' }}>v{version.version_number}</span>
            {version.published_at ? (
              <span className="badge badge-success">Published</span>
            ) : (
              <span className="badge badge-warning">Draft</span>
            )}
            <span className="text-secondary" style={{ fontSize: '12px' }}>
              Created by {version.created_by_email} on {new Date(version.created_at).toLocaleString()}
              {version.published_at && ` · Published ${new Date(version.published_at).toLocaleString()}`}
            </span>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={onStartEdit}>
              Edit
            </button>
          )}
        </div>
      )}

      {/* Draft created banner */}
      {isEditing && draftCreatedFrom && (
        <div className="config-draft-banner">
          <span style={{ fontSize: '15px' }}>ℹ️</span>
          Editing created a new draft (v{version.version_number}). This version will only go live after you Save & Publish it.
        </div>
      )}

      {/* Alerts */}
      {error && <div className="alert alert-error" style={{ margin: '0 24px', marginTop: '16px' }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ margin: '0 24px', marginTop: '16px' }}>{success}</div>}

      {/* Validation summary */}
      {showValidation && errors.length > 0 && (
        <div style={{ margin: '0 24px', marginTop: '16px' }}>
          <ValidationSummary errors={errors} onNavigate={handleNavigateToError} onDismiss={() => setShowValidation(false)} />
        </div>
      )}

      <div className="config-editor-body">
        {/* Sidebar */}
        <div className="config-sidebar">
          <div className="config-sidebar-header">Config Structure</div>
          {processes.map((p, pi) => (
            <div key={pi}>
              <div
                className={`config-sidebar-process ${selectedType === 'process' && selectedProcess === pi ? 'active' : ''}`}
                onClick={() => selectProcess(pi)}
              >
                <span style={{ marginRight: '6px', fontSize: '10px' }}>
                  {expandedProcesses.has(pi) ? '▾' : '▸'}
                </span>
                {p.process_name || `Process ${pi + 1}`}
                <span className="config-sidebar-count">{p.steps.length} step{p.steps.length !== 1 ? 's' : ''}</span>
              </div>
              {expandedProcesses.has(pi) && p.steps.map((s, si) => (
                <div
                  key={si}
                  className={`config-sidebar-step ${selectedType === 'step' && selectedProcess === pi && selectedStep === si ? 'active' : ''}`}
                  onClick={() => selectStep(pi, si)}
                >
                  {s.label || `Step ${si + 1}`}
                </div>
              ))}
            </div>
          ))}
          <div className="config-sidebar-actions">
            {isEditing && (
              <button className="btn btn-ghost btn-sm" style={{ width: '100%', textAlign: 'left' }}
                onClick={addProcess} disabled={atProcessLimit}
                title={atProcessLimit ? `Process limit (${maxProcesses}) reached` : undefined}>
                + Add process
              </button>
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div className="config-detail">
          {selectedType === 'process' && currentProcess && (
            <div>
              <div className="config-detail-header">
                <div>
                  <div className="config-detail-breadcrumb">{currentProcess.process_name || `Process ${selectedProcess + 1}`}</div>
                  <div className="flex items-center gap-8" style={{ marginTop: '4px' }}>
                    {readOnly && isAdmin ? (
                      <label className="toggle" style={{ marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={currentProcess.enabled}
                          onChange={e => toggleField(
                            `processes[${selectedProcess}].enabled`,
                            e.target.checked,
                            processes,
                          )}
                        />
                        <div className="toggle-track" /><div className="toggle-thumb" />
                        <span className="toggle-label">{currentProcess.enabled ? 'Enabled' : 'Disabled'}</span>
                      </label>
                    ) : (
                      <span className={`badge ${currentProcess.enabled ? 'badge-success' : 'badge-danger'}`}>
                        {currentProcess.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>
                </div>
                {isEditing && processes.length > 1 && (
                  <button className="btn btn-ghost btn-sm text-danger" onClick={() => removeProcess(selectedProcess)}>
                    Remove process
                  </button>
                )}
              </div>
              <ProcessForm
                process={currentProcess}
                processIndex={selectedProcess}
                onUpdate={updateProcess}
                readOnly={readOnly}
                showEnabled={isEditing}
              />
              {isEditing && (
                <button className="btn btn-secondary btn-sm" style={{ marginTop: '16px' }}
                  onClick={() => addStep(selectedProcess)}>
                  + Add step to this process
                </button>
              )}
            </div>
          )}

          {selectedType === 'step' && currentStep && (
            <div>
              <div className="config-detail-header">
                <div>
                  <div className="config-detail-breadcrumb">
                    {currentProcess.process_name || `Process ${selectedProcess + 1}`} → Step {selectedStep + 1} of {currentProcess.steps.length}
                  </div>
                  <h3 style={{ margin: '4px 0 0 0' }}>{currentStep.label || `Step ${selectedStep + 1}`}</h3>
                </div>
                <div className="flex items-center gap-8">
                  {readOnly ? (
                    <>
                      {isAdmin ? (
                        <>
                          <label className="toggle" style={{ marginBottom: 0 }}>
                            <input
                              type="checkbox"
                              checked={currentStep.enabled}
                              onChange={e => toggleField(
                                `processes[${selectedProcess}].steps[${selectedStep}].enabled`,
                                e.target.checked,
                                processes,
                              )}
                            />
                            <div className="toggle-track" /><div className="toggle-thumb" />
                            <span className="toggle-label">{currentStep.enabled ? 'Enabled' : 'Disabled'}</span>
                          </label>
                          <label className="toggle" style={{ marginBottom: 0 }}>
                            <input
                              type="checkbox"
                              checked={!!currentStep.debug}
                              onChange={e => toggleField(
                                `processes[${selectedProcess}].steps[${selectedStep}].debug`,
                                e.target.checked,
                                processes,
                              )}
                            />
                            <div className="toggle-track" /><div className="toggle-thumb" />
                            <span className="toggle-label">Debug</span>
                          </label>
                        </>
                      ) : (
                        <>
                          <span className={`badge ${currentStep.enabled ? 'badge-success' : 'badge-danger'}`}>
                            {currentStep.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          {currentStep.debug && <span className="badge badge-warning">Debug</span>}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <label className="toggle" style={{ marginBottom: 0 }}>
                        <input type="checkbox" checked={currentStep.enabled}
                          onChange={e => updateStep(selectedProcess, selectedStep, 'enabled', e.target.checked)} />
                        <div className="toggle-track" /><div className="toggle-thumb" />
                        <span className="toggle-label">{currentStep.enabled ? 'Enabled' : 'Disabled'}</span>
                      </label>
                      {isEditing && currentProcess.steps.length > 1 && (
                        <button className="btn btn-ghost btn-sm text-danger"
                          onClick={() => removeStep(selectedProcess, selectedStep)}>
                          Remove step
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <StepForm
                step={currentStep}
                processIndex={selectedProcess}
                stepIndex={selectedStep}
                mode="editor"
                readOnly={readOnly}
                isAdmin={isAdmin}
                onUpdateStep={(field, value) => {
                  if (readOnly) {
                    // In read mode, toggles use the toggle API
                    const path = `processes[${selectedProcess}].steps[${selectedStep}].${field}`;
                    toggleField(path, value, processes);
                  } else {
                    updateStep(selectedProcess, selectedStep, field, value);
                  }
                }}
                onUpdateIdentifier={(ii, field, value) => updateIdentifier(selectedProcess, selectedStep, ii, field, value)}
                onAddIdentifier={() => addIdentifier(selectedProcess, selectedStep)}
                onRemoveIdentifier={(ii) => removeIdentifier(selectedProcess, selectedStep, ii)}
                onUpdateCondition={(ci, field, value) => {
                  if (readOnly && field === 'enabled') {
                    const path = `processes[${selectedProcess}].steps[${selectedStep}].conditions[${ci}].enabled`;
                    toggleField(path, value, processes);
                  } else {
                    updateCondition(selectedProcess, selectedStep, ci, field, value);
                  }
                }}
                onAddCondition={() => addCondition(selectedProcess, selectedStep)}
                onRemoveCondition={(ci) => removeCondition(selectedProcess, selectedStep, ci)}
                onUpdateCustomField={(fi, field, value) => updateCustomField(selectedProcess, selectedStep, fi, field, value)}
                onAddCustomField={() => addCustomField(selectedProcess, selectedStep)}
                onRemoveCustomField={(fi) => removeCustomField(selectedProcess, selectedStep, fi)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Discard confirmation dialog */}
      {showDiscardDialog && (
        <div className="dialog-overlay" onClick={() => setShowDiscardDialog(false)}>
          <div className="card" style={{ width: '420px', boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <span className="card-title">Discard Draft?</span>
            </div>
            <div className="card-body">
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
                Discard all changes{draftCreatedFrom ? ` since v${draftCreatedFrom}` : ''}? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setShowDiscardDialog(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => { setShowDiscardDialog(false); handleDiscard(); }} disabled={saving}>
                  {saving ? 'Discarding…' : 'Discard Draft'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
